<template>
  <div class="tsa-page py-3">
    <header class="tsa-toolbar">
      <div class="tsa-toolbar__title">
        <span class="tsa-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Tele-Sales Accounts</h1>
          <p>Incentive earnings per staff member — scoped to the selected period</p>
        </div>
      </div>
      <div class="tsa-toolbar__actions">
        <CButton size="sm" class="tsa-export-btn" :disabled="isExporting || isLoading" @click="exportXlsx">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span>{{ isExporting ? 'Exporting…' : 'Export XLSX' }}</span>
        </CButton>
        <CButton size="sm" class="tsa-refresh-btn" :class="{ 'is-loading': isLoading }" :disabled="isLoading" @click="loadAccounts">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="tsa-refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="tsa-alert">{{ errorMessage }}</CAlert>

    <section class="tsa-tiles">
      <article class="tsa-tile tsa-tile--balance">
        <div class="tsa-tile__label">Total Earned ({{ periodLabel }})</div>
        <div class="tsa-tile__value">₹{{ formatAmount(totalBalance) }}</div>
        <div class="tsa-tile__sub">Sum across every agent for the selected period</div>
      </article>
      <article class="tsa-tile tsa-tile--count">
        <div class="tsa-tile__label">Staff With Activity</div>
        <div class="tsa-tile__value">{{ staffCount }}</div>
        <div class="tsa-tile__sub">Agents with at least one entry in this period</div>
      </article>
    </section>

    <section class="tsa-section">
      <header class="tsa-section__head">
        <h2>Filters</h2>
        <small class="tsa-section__hint">{{ resultsHint }}</small>
      </header>
      <div class="tsa-section__body">
        <div class="tsa-filters">
          <div class="tsa-field tsa-field--range">
            <label class="tsa-label">Period</label>
            <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
          </div>
          <div class="tsa-field tsa-field--search">
            <label class="tsa-label">Search</label>
            <input
              v-model="search"
              type="search"
              class="tsa-input"
              placeholder="Name, designation, or employee code…"
            >
          </div>
          <div class="tsa-field tsa-field--action">
            <label class="tsa-label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="tsa-apply-btn" :disabled="isLoading" @click="applyFilters">{{ isLoading ? 'Loading…' : 'Apply' }}</button>
          </div>
          <div class="tsa-field tsa-field--action">
            <label class="tsa-label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="tsa-reset-btn" :disabled="isLoading" @click="resetFilters">Reset</button>
          </div>
        </div>
      </div>
    </section>

    <div class="tsa-view-bar" role="tablist" aria-label="View mode">
      <button
        v-for="opt in viewOptions"
        :key="opt.value"
        type="button"
        role="tab"
        :class="['tsa-view', { 'is-active': view === opt.value }]"
        :aria-selected="view === opt.value"
        @click="view = opt.value"
      >
        <span class="tsa-view__label">{{ opt.label }}</span>
      </button>
    </div>

    <!-- Card view -->
    <template v-if="view === 'cards'">
      <section v-if="isLoading && !accounts.length" class="tsa-grid tsa-grid--skeleton" aria-hidden="true">
        <article v-for="n in 6" :key="`sk-${n}`" class="tsa-card tsa-card--skeleton">
          <div class="sk-head">
            <div class="sk-avatar"></div>
            <div class="sk-lines">
              <div class="sk-line sk-line--wide"></div>
              <div class="sk-line sk-line--mid"></div>
            </div>
          </div>
          <div class="sk-balance"></div>
          <div class="sk-line sk-line--narrow"></div>
          <div class="sk-line sk-line--narrow"></div>
        </article>
      </section>

      <section v-else-if="filteredAccounts.length" class="tsa-grid">
        <article
          v-for="row in filteredAccounts"
          :key="row.id"
          class="tsa-card"
          @click="openAccount(row)"
        >
          <div class="tsa-card__head">
            <div class="tsa-avatar" :style="{ background: avatarColor(row.name) }">{{ initials(row.name) }}</div>
            <div class="tsa-card__identity">
              <div class="tsa-card__name">{{ row.name }}</div>
              <div class="tsa-card__sub">
                <span v-if="row.designation">{{ row.designation }}</span>
                <span v-if="row.employee_code" class="tsa-card__code">#{{ row.employee_code }}</span>
              </div>
            </div>
          </div>
          <div class="tsa-card__balance">
            <span class="tsa-card__currency">₹</span>
            <span class="tsa-card__amount">{{ formatAmount(row.balance) }}</span>
          </div>
          <div class="tsa-card__meta">
            <div class="tsa-card__meta-row">
              <span class="tsa-card__meta-label">Entries</span>
              <span class="tsa-card__meta-value">{{ row.entry_count }}</span>
            </div>
            <div class="tsa-card__meta-row">
              <span class="tsa-card__meta-label">Last activity</span>
              <span class="tsa-card__meta-value">{{ formatDate(row.last_entry_at) }}</span>
            </div>
          </div>
          <div class="tsa-card__cta">View transactions →</div>
        </article>
      </section>

      <div v-else class="tsa-empty">
        <div class="tsa-empty__glyph" aria-hidden="true">∅</div>
        <p>No activity in this period.</p>
        <p class="tsa-empty__hint">Widen the date range, or open Incentive Preview and click <strong>+ Credit</strong> on eligible rows.</p>
      </div>
    </template>

    <!-- Table view -->
    <BaseTable
      v-else
      title="Staff Accounts"
      :items="filteredAccounts"
      :columns="tableColumns"
      :loading="isLoading"
      :sortable="false"
      :show-count="false"
      empty-text="No activity in this period."
      hover
      @row-click="openAccount"
    >
      <template #toolbar>
        <span class="tsa-table-hint">{{ resultsHint }}</span>
      </template>
      <template #cell-staff="{ row }">
        <div class="tsa-cell-staff">
          <div class="tsa-avatar tsa-avatar--sm" :style="{ background: avatarColor(row.name) }">{{ initials(row.name) }}</div>
          <div class="tsa-cell-staff__text">
            <div class="tsa-cell-staff__name">{{ row.name }}</div>
            <div class="tsa-cell-staff__sub">
              <span v-if="row.designation">{{ row.designation }}</span>
              <span v-if="row.employee_code" class="tsa-cell-staff__code">#{{ row.employee_code }}</span>
              <span v-if="!row.designation && !row.employee_code">—</span>
            </div>
          </div>
        </div>
      </template>
      <template #cell-last_entry_at="{ value }">
        {{ formatDate(value) }}
      </template>
      <template #cell-balance="{ value }">
        <span class="tsa-cell-balance">₹{{ formatAmount(value) }}</span>
      </template>
    </BaseTable>

    <CModal :show.sync="modalOpen" size="lg" :centered="true" :title="selectedName" class="view-account-modal">
      <div v-if="selectedAccount" class="va-body">
        <div class="va-section">
          <div class="va-row"><span class="va-key">Name</span><span class="va-val">{{ selectedAccount.user.name || '—' }}</span></div>
          <div class="va-row"><span class="va-key">Designation</span><span class="va-val">{{ selectedAccount.user.designation || '—' }}</span></div>
          <div class="va-row"><span class="va-key">Employee Code</span><span class="va-val">{{ selectedAccount.user.employee_code || '—' }}</span></div>
          <div class="va-row"><span class="va-key">Email</span><span class="va-val">{{ selectedAccount.user.email || '—' }}</span></div>
        </div>

        <div class="va-grid">
          <div class="va-row">
            <span class="va-key">Period Balance</span>
            <span class="va-val"><span class="va-pill va-pill--balance">₹{{ formatAmount(selectedAccount.balance) }}</span></span>
          </div>
          <div class="va-row"><span class="va-key">Entries In Period</span><span class="va-val">{{ selectedAccount.entry_count }}</span></div>
          <div class="va-row"><span class="va-key">First Entry</span><span class="va-val">{{ formatDate(selectedAccount.first_entry_at) }}</span></div>
          <div class="va-row"><span class="va-key">Latest Entry</span><span class="va-val">{{ formatDate(selectedAccount.last_entry_at) }}</span></div>
        </div>

        <div class="va-items-wrap">
          <table class="va-items">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Patient (OP)</th>
                <th>Note</th>
                <th class="va-items__amount">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!selectedAccount.recent.length">
                <td colspan="5" class="va-items__empty">No transactions in this period.</td>
              </tr>
              <tr v-for="tx in selectedAccount.recent" :key="tx.id">
                <td>{{ formatDateTime(tx.created_at) }}</td>
                <td><span class="va-pill" :class="typePillClass(tx.entry_type)">{{ typeLabel(tx.entry_type) }}</span></td>
                <td>
                  <template v-if="tx.attribution && tx.attribution.patient">
                    {{ tx.attribution.patient.patient_name }} <span class="va-items__muted">(OP {{ tx.attribution.patient.op_no }})</span>
                  </template>
                  <template v-else>—</template>
                </td>
                <td>{{ tx.note || '—' }}</td>
                <td class="va-items__amount" :class="Number(tx.amount) >= 0 ? 'va-items__amount--credit' : 'va-items__amount--debit'">
                  {{ Number(tx.amount) >= 0 ? '+' : '' }}₹{{ formatAmount(tx.amount) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-else class="va-loading">Loading account…</div>

      <template #footer>
        <CButton color="secondary" variant="outline" @click="modalOpen = false">Close</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import axios from 'axios'
import DateRangePicker from '@/components/DateRangePicker.vue'
import BaseTable from '@/components/BaseTable.vue'

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const AVATAR_COLORS = [
  '#0e5b46', '#1d4ed8', '#9333ea', '#b91c1c', '#b45309',
  '#15803d', '#0369a1', '#7c2d12', '#4338ca', '#be185d'
]

function startOfMonthIso () {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

function todayIso () {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatDate (value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`
}

function formatDateTime (value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${formatDate(value)}, ${hh}:${mm}`
}

export default {
  name: 'TeleSalesAccounts',
  components: { DateRangePicker, BaseTable },
  data () {
    return {
      accounts: [],
      totalBalance: 0,
      staffCount: 0,
      isLoading: false,
      isExporting: false,
      errorMessage: '',
      search: '',
      view: 'cards',
      viewOptions: [
        { value: 'cards', label: 'Card View' },
        { value: 'table', label: 'Table View' }
      ],
      filters: {
        fromDate: startOfMonthIso(),
        toDate: todayIso()
      },
      appliedFromDate: startOfMonthIso(),
      appliedToDate: todayIso(),
      modalOpen: false,
      selectedAccount: null
    }
  },
  computed: {
    dateRange: {
      get () {
        return { fromDate: this.filters.fromDate, toDate: this.filters.toDate }
      },
      set (value) {
        this.filters.fromDate = (value && value.fromDate) || ''
        this.filters.toDate = (value && value.toDate) || ''
      }
    },
    periodLabel () {
      const from = this.appliedFromDate ? formatDate(this.appliedFromDate) : 'Any'
      const to = this.appliedToDate ? formatDate(this.appliedToDate) : 'Today'
      if (this.appliedFromDate && this.appliedToDate && this.appliedFromDate === this.appliedToDate) {
        return from
      }
      return `${from} – ${to}`
    },
    resultsHint () {
      if (this.isLoading && !this.accounts.length) return 'Loading…'
      const total = this.filteredAccounts.length
      if (!total) return 'No matches'
      return `${total} ${total === 1 ? 'agent' : 'agents'}`
    },
    filteredAccounts () {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.accounts
      return this.accounts.filter((r) => {
        const haystack = [
          r.name,
          r.designation,
          r.employee_code,
          r.email
        ].filter(Boolean).join(' ').toLowerCase()
        return haystack.includes(q)
      })
    },
    tableColumns () {
      return [
        { key: 'staff', label: 'Staff' },
        { key: 'entry_count', label: 'Entries', align: 'right', width: '110px' },
        { key: 'last_entry_at', label: 'Last Activity', width: '160px' },
        { key: 'balance', label: 'Balance', align: 'right', width: '160px' }
      ]
    },
    selectedName () {
      if (!this.selectedAccount) return 'Account Details'
      return `${this.selectedAccount.user.name} — ${this.periodLabel}`
    }
  },
  created () {
    this.loadAccounts()
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    parseError (error, fallback) {
      const data = error && error.response && error.response.data
      if (data) {
        if (typeof data === 'string') return data
        if (data.message) return data.message
      }
      return fallback
    },
    rangeParams () {
      const params = { token: this.authToken() }
      if (this.appliedFromDate) params.from_date = this.appliedFromDate
      if (this.appliedToDate) params.to_date = this.appliedToDate
      return params
    },
    applyFilters () {
      this.appliedFromDate = this.filters.fromDate || ''
      this.appliedToDate = this.filters.toDate || ''
      this.loadAccounts()
    },
    resetFilters () {
      this.filters.fromDate = startOfMonthIso()
      this.filters.toDate = todayIso()
      this.search = ''
      this.applyFilters()
    },
    loadAccounts () {
      this.isLoading = true
      this.errorMessage = ''
      axios.get(`${this.$apiAdress}/api/tele-sales-accounts`, {
        params: this.rangeParams()
      })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.accounts = Array.isArray(data.data) ? data.data : []
          this.totalBalance = Number(data.total_balance) || 0
          this.staffCount = Number(data.staff_count) || 0
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to load accounts.')
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    // Mirror of IncentivePreview.vue's exportXlsx — fetch as a blob so
    // the download honours the active token + date range, then trigger a
    // synthetic <a> click. window.open with a query token would also work
    // but exposes the token in the browser's URL bar / history.
    exportXlsx () {
      this.isExporting = true
      this.errorMessage = ''
      axios.get(`${this.$apiAdress}/api/tele-sales-accounts/export`, {
        params: this.rangeParams(),
        responseType: 'blob'
      })
        .then((response) => {
          const blob = new Blob(
            [response.data],
            { type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
          )
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          const from = this.appliedFromDate || 'all'
          const to = this.appliedToDate || new Date().toISOString().slice(0, 10)
          link.href = url
          link.download = `tele_sales_accounts_${from}_to_${to}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Export failed.')
        })
        .finally(() => {
          this.isExporting = false
        })
    },
    openAccount (row) {
      this.modalOpen = true
      this.selectedAccount = null
      axios.get(`${this.$apiAdress}/api/tele-sales-accounts/${row.id}`, {
        params: this.rangeParams()
      })
        .then((response) => {
          this.selectedAccount = response && response.data ? response.data : null
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to load account.')
          this.modalOpen = false
        })
    },
    formatAmount (v) {
      const n = Number(v) || 0
      return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDate,
    formatDateTime,
    initials (name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      const a = parts[0] ? parts[0][0] : ''
      const b = parts[1] ? parts[1][0] : ''
      return `${a}${b}`.toUpperCase() || '?'
    },
    avatarColor (name) {
      if (!name) return AVATAR_COLORS[0]
      let hash = 0
      for (let i = 0; i < name.length; i++) hash = (hash + name.charCodeAt(i)) % AVATAR_COLORS.length
      return AVATAR_COLORS[hash]
    },
    typeLabel (type) {
      if (type === 'incentive_credit') return 'Incentive'
      if (type === 'manual_adjustment') return 'Adjustment'
      if (type === 'payout') return 'Payout'
      return type
    },
    typePillClass (type) {
      if (type === 'incentive_credit') return 'va-pill--credit'
      if (type === 'payout') return 'va-pill--payout'
      return 'va-pill--neutral'
    }
  }
}
</script>

<style scoped>
.tsa-page { padding: 0.25rem 0; }

/* ── Toolbar ── */
.tsa-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 0.75rem; padding: 0.25rem 0.25rem 1rem;
}
.tsa-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }
.tsa-toolbar__title h1 { margin: 0; font-size: 1.15rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.01em; }
.tsa-toolbar__title p { margin: 0.1rem 0 0; font-size: 0.78rem; color: #6b7280; }
.tsa-toolbar__accent { width: 4px; height: 32px; border-radius: 4px; background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%); }

.tsa-toolbar__actions { display: inline-flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }

.tsa-toolbar__actions >>> .btn.tsa-refresh-btn,
.tsa-toolbar__actions >>> .btn.tsa-export-btn {
  display: inline-flex; align-items: center; gap: 0.45rem;
  height: 34px; padding: 0 0.85rem; border-radius: 8px;
  font-size: 0.82rem; font-weight: 600;
  background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
.tsa-toolbar__actions >>> .btn.tsa-refresh-btn:hover:not(:disabled),
.tsa-toolbar__actions >>> .btn.tsa-export-btn:hover:not(:disabled) {
  border-color: #16a07a; color: #0e5b46; background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}
.tsa-toolbar__actions >>> .btn.tsa-refresh-btn:disabled,
.tsa-toolbar__actions >>> .btn.tsa-export-btn:disabled { opacity: 1; background: #f8fafc; color: #4b5563; cursor: progress; }
.tsa-toolbar__actions >>> .btn.tsa-export-btn svg { flex-shrink: 0; display: block; color: #16a07a; }
.tsa-refresh-btn__icon { flex-shrink: 0; display: block; color: #16a07a; transition: transform 0.4s ease; }
.tsa-toolbar__actions >>> .btn.tsa-refresh-btn:hover:not(:disabled) .tsa-refresh-btn__icon { transform: rotate(60deg); }
.tsa-toolbar__actions >>> .btn.tsa-refresh-btn.is-loading .tsa-refresh-btn__icon { animation: tsa-spin 0.9s linear infinite; }
@keyframes tsa-spin { to { transform: rotate(360deg); } }

.tsa-alert { margin: 0 0 0.75rem; border-radius: 10px; }

/* ── Tiles ── */
.tsa-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;
}
.tsa-tile {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  display: flex; flex-direction: column; gap: 0.25rem;
  min-width: 0;
}
.tsa-tile__label { font-size: 0.74rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.tsa-tile__value { font-size: 1.85rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.02em; }
.tsa-tile__sub { font-size: 0.78rem; color: #6b7280; }
.tsa-tile--balance .tsa-tile__value { color: #0e5b46; }

/* ── Filter section card ── */
.tsa-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}
.tsa-section__head {
  display: flex; align-items: center; justify-content: space-between; gap: 0.5rem;
  padding: 0.7rem 1rem; border-bottom: 1px solid #eef1f7;
  background: #fbfcfe; border-radius: 12px 12px 0 0;
}
.tsa-section__head h2 { margin: 0; font-size: 0.95rem; font-weight: 700; color: #1f2a44; }
.tsa-section__hint { color: #6b7280; font-size: 0.78rem; }
.tsa-section__body { padding: 1rem; }

.tsa-label { display: block; margin-bottom: 0.3rem; font-size: 0.78rem; font-weight: 600; color: #4f5560; }

.tsa-filters {
  display: flex; flex-wrap: wrap; gap: 0.85rem; align-items: flex-end;
}
.tsa-field { display: flex; flex-direction: column; flex: 1 1 220px; min-width: 0; }
.tsa-field--range { flex: 1 1 320px; min-width: 280px; }
.tsa-field--search { flex: 2 1 260px; }
.tsa-field--action { flex: 0 0 auto; min-width: 110px; }

.tsa-input {
  width: 100%; height: 36px; padding: 0 0.95rem;
  border: 1px solid #d8dde6; border-radius: 8px;
  font-size: 0.85rem; color: #2f3640; background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.tsa-input:focus {
  outline: none; border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.tsa-apply-btn {
  height: 36px; padding: 0 1.1rem;
  border-radius: 8px; border: 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff; font-weight: 600; font-size: 0.85rem;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}
.tsa-apply-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05); }
.tsa-apply-btn:disabled { opacity: 0.55; cursor: progress; }

.tsa-reset-btn {
  height: 36px; padding: 0 1rem;
  border-radius: 8px;
  background: #ffffff; border: 1px solid #d8dde6;
  color: #1f2a44; font-weight: 600; font-size: 0.85rem; cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.tsa-reset-btn:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }

/* ── View toggle (pill tab strip) ── */
.tsa-view-bar {
  display: flex;
  gap: 0.25rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
  padding: 0.55rem 0.3rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  width: fit-content;
  max-width: 100%;
  margin: 0 0 1rem;
  -webkit-overflow-scrolling: touch;
}
.tsa-view-bar::-webkit-scrollbar { display: none; }
.tsa-view {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; gap: 0.4rem;
  padding: 0.55rem 1rem;
  border: none; outline: none;
  background: transparent;
  border-radius: 999px;
  font-size: 0.82rem; font-weight: 600;
  color: #64748b; cursor: pointer; white-space: nowrap;
  transition: all 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}
.tsa-view:focus { outline: none; box-shadow: none; }
.tsa-view:focus-visible { box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.25); }
.tsa-view:hover:not(.is-active) { background: #f1f5f9; color: #334155; }
.tsa-view.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28);
}
.tsa-view__label { line-height: 1; }

/* ── Card grid ── */
.tsa-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 1rem;
}
.tsa-card {
  background: linear-gradient(160deg, #ffffff 0%, #fbfcfe 100%);
  border: 1px solid #e5e9f2;
  border-radius: 16px;
  padding: 1.2rem 1.25rem 1rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.05);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  display: flex; flex-direction: column; gap: 0.85rem;
}
.tsa-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1);
  border-color: #16a07a;
}
.tsa-card__head { display: flex; align-items: center; gap: 0.75rem; }
.tsa-avatar {
  width: 44px; height: 44px;
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 700; font-size: 0.95rem;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}
.tsa-avatar--sm { width: 32px; height: 32px; font-size: 0.78rem; }
.tsa-card__identity { min-width: 0; flex: 1; }
.tsa-card__name { font-size: 0.98rem; font-weight: 700; color: #1f2a44; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tsa-card__sub { display: flex; gap: 0.5rem; font-size: 0.76rem; color: #6b7280; flex-wrap: wrap; }
.tsa-card__code { color: #94a3b8; }
.tsa-card__balance {
  display: flex; align-items: baseline; gap: 0.2rem;
  padding: 0.8rem 0;
  border-top: 1px dashed #e5e9f2;
  border-bottom: 1px dashed #e5e9f2;
}
.tsa-card__currency { font-size: 0.95rem; font-weight: 700; color: #0e5b46; }
.tsa-card__amount { font-size: 1.7rem; font-weight: 800; color: #0e5b46; letter-spacing: -0.02em; }
.tsa-card__meta { display: flex; flex-direction: column; gap: 0.35rem; }
.tsa-card__meta-row { display: flex; justify-content: space-between; font-size: 0.78rem; }
.tsa-card__meta-label { color: #6b7280; }
.tsa-card__meta-value { color: #1f2a44; font-weight: 600; }
.tsa-card__cta {
  text-align: right; font-size: 0.75rem; font-weight: 600;
  color: #16a07a; letter-spacing: 0.02em;
  border-top: 1px solid #f1f5f9; padding-top: 0.7rem;
}

/* ── Skeleton cards ── */
.tsa-grid--skeleton .tsa-card--skeleton {
  cursor: default; pointer-events: none;
  background: #ffffff;
}
.tsa-card--skeleton .sk-head { display: flex; align-items: center; gap: 0.75rem; }
.sk-avatar { width: 44px; height: 44px; border-radius: 50%; background: #eef1f7; flex-shrink: 0; }
.sk-lines { flex: 1; display: flex; flex-direction: column; gap: 0.4rem; }
.sk-line { height: 10px; border-radius: 6px; background: #eef1f7; }
.sk-line--wide   { width: 70%; }
.sk-line--mid    { width: 45%; }
.sk-line--narrow { width: 90%; }
.sk-balance { height: 32px; border-radius: 8px; background: #eef1f7; margin: 0.4rem 0; }
.tsa-card--skeleton .sk-avatar,
.tsa-card--skeleton .sk-line,
.tsa-card--skeleton .sk-balance {
  background: linear-gradient(90deg, #eef1f7 0%, #f6f8fb 50%, #eef1f7 100%);
  background-size: 200% 100%;
  animation: tsa-shimmer 1.2s ease-in-out infinite;
}
@keyframes tsa-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Empty state ── */
.tsa-empty {
  background: #ffffff;
  border: 1px dashed #d8dde6;
  border-radius: 12px;
  padding: 2.25rem 1.5rem;
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
}
.tsa-empty__glyph { font-size: 1.6rem; color: #cbd5e1; margin-bottom: 0.4rem; }
.tsa-empty__hint { font-size: 0.8rem; color: #94a3b8; margin-top: 0.5rem; }

/* ── Table view extras ── */
.tsa-table-hint { font-size: 0.78rem; color: #6b7280; }
.tsa-cell-staff { display: flex; align-items: center; gap: 0.6rem; }
.tsa-cell-staff__text { min-width: 0; }
.tsa-cell-staff__name { font-size: 0.85rem; font-weight: 700; color: #1f2a44; }
.tsa-cell-staff__sub { display: flex; gap: 0.4rem; font-size: 0.74rem; color: #6b7280; flex-wrap: wrap; }
.tsa-cell-staff__code { color: #94a3b8; }
.tsa-cell-balance { font-weight: 700; color: #0e5b46; }

/* ── View Account Modal — ports the vo-* design-system contract ── */
.view-account-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}
.view-account-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.95rem 1.15rem;
}
.view-account-modal >>> .modal-header .modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
}
.view-account-modal >>> .modal-body {
  padding: 1.1rem;
  background: #fbfcfe;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}
.view-account-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
}
.view-account-modal >>> .modal-footer .btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.45rem 1.1rem;
  margin: 0;
  min-width: 90px;
}

.va-body { display: flex; flex-direction: column; gap: 0.85rem; }
.va-section,
.va-grid,
.va-items-wrap {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: hidden;
}
.va-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 0.75rem;
  padding: 0.7rem 0.95rem;
  border-bottom: 1px solid #eef1f7;
  align-items: center;
}
.va-row:last-child { border-bottom: 0; }
.va-section .va-row:nth-child(even) { background: #fafbfd; }
.va-key { font-size: 0.82rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.01em; }
.va-val { font-size: 0.82rem; color: #374151; word-break: break-word; }

.va-grid { display: grid; grid-template-columns: 1fr 1fr; }
.va-grid .va-row { grid-template-columns: 160px 1fr; border-right: 1px solid #eef1f7; }
.va-grid .va-row:nth-child(2n) { border-right: 0; }
.va-grid .va-row:nth-last-child(-n+2) { border-bottom: 0; }
.va-grid .va-row:nth-child(4n-1),
.va-grid .va-row:nth-child(4n) { background: #fafbfd; }

.va-items { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 0.82rem; }
.va-items th {
  background: #ffffff;
  padding: 0.7rem 0.95rem;
  text-align: left;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2a44;
  border-bottom: 1px solid #eef1f7;
  letter-spacing: -0.01em;
}
.va-items td { padding: 0.7rem 0.95rem; border-bottom: 1px solid #eef1f7; color: #374151; }
.va-items tr:last-child td { border-bottom: 0; }
.va-items__empty { text-align: center; color: #6b7280; font-style: italic; }
.va-items__amount { text-align: right; font-weight: 700; }
.va-items__amount--credit { color: #166534; }
.va-items__amount--debit { color: #b91c1c; }
.va-items__muted { color: #6b7280; }

.va-pill {
  display: inline-block;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.va-pill--balance { background: #dcfce7; color: #0e5b46; }
.va-pill--credit  { background: #dcfce7; color: #166534; }
.va-pill--payout  { background: #fee2e2; color: #991b1b; }
.va-pill--neutral { background: #e5e7eb; color: #374151; }

.va-loading { padding: 2rem; text-align: center; color: #64748b; }
</style>
